/**
 * web/src/lib/errorCopy.ts — error messages are Title Case, and keep their
 * tone (card QR9phRSRQHNx, founder batch 2026-10-13).
 *
 * Two jobs:
 *   1. `titleCase()` — the shared casing pass. Minor words (a, or, to, the,
 *      and, ...) stay lowercase mid-sentence the way a title does; em-dash
 *      separators survive untouched, so "— try again" keeps its tone.
 *   2. `formatApiError()` — decode the bridge's thrown
 *      `JSON.stringify({error:{code,message}})` and return the message in
 *      Title Case with the raw code appended unchanged: the code is an
 *      identifier, never re-cased.
 */

/**
 * The one address a person can write to when the product breaks, and the one
 * sentence we answer a SYSTEM failure with (Jonathan 2026-10-24).
 *
 * This is deliberately NOT title-cased and NOT a code: the API-error casing
 * pass exists for ordinary messages, while this copy is the "we broke, here is
 * a human" line. `formatApiError` passes it through verbatim, because
 * "Apologies, We’ve Encountered an Issue Here." reads like a form letter.
 *
 * Use it for failures that are OUR problem — a dead network, an HTML answer
 * where JSON was expected, a 5xx — never for validation (a wrong password is
 * still "That email or handle and password did not match. Try again.").
 */
export const SUPPORT_EMAIL = 'support@pearconnected.io';
export const GRACEFUL_FAILURE =
  `Apologies, we’ve encountered an issue here. Give us some time and we’ll make it right. ` +
  `Contact ${SUPPORT_EMAIL} to provide more details.`;

/** The same apology with a short reference the person can quote in the mail —
 *  a status code or an error code, never a stack trace. */
export function gracefulFailure(reference?: string | null): string {
  return reference ? `${GRACEFUL_FAILURE} (${reference})` : GRACEFUL_FAILURE;
}

/** Words that stay lowercase mid-title (the first word is always capitalised).
 *  Articles, conjunctions and prepositions only — verbs and pronouns are NOT
 *  minor words, because "Could Not be Loaded" reads as a typo next to the
 *  capitalised "Not" beside it (card QR9phRSRQHNx). */
const MINOR = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'nor', 'to', 'of', 'in', 'on', 'at',
  'for', 'with', 'from', 'into', 'per', 'via', 'as', 'up',
]);

/** Acronyms already in their display form — left exactly as written so
 *  "the api" becomes "The API" and never "The Api". */
const ACRONYM = /^[A-Z]{2,5}$/;

/** Capitalise the first letter of a word, leave the rest alone (so acronyms
 *  like "Groq" or "API" inside a message are never mangled). */
function cap(word: string): string {
  return word.length ? word[0].toUpperCase() + word.slice(1) : word;
}

/**
 * Title Case a sentence for display: every word capitalised except minor
 * words mid-sentence. Punctuation (—, :, ()) is preserved verbatim, and a
 * word is only cased at a word boundary — never inside an identifier, so
 * `gift_concierge` travels untouched while `could not` becomes `Could Not`.
 */
export function titleCase(input: string): string {
  let first = true;
  // Underscores stay in the token: an identifier is one word, not two.
  return input.replace(/[A-Za-z][A-Za-z0-9_'’-]*/g, (word) => {
    const wasFirst = first;
    first = false;
    if (word.includes('_')) return word;              // identifier — verbatim
    if (ACRONYM.test(word)) return word;              // already an acronym
    const lower = word.toLowerCase();
    if (!wasFirst && MINOR.has(lower)) return lower;
    return cap(word);
  });
}

/** Decode the bridge's thrown error JSON and title-case the human message.
 *  Falls back to title-casing the raw message when it is not our shape. */
export function formatApiError(e: unknown): string {
  if (!(e instanceof Error) || !e.message) return 'Something Went Wrong';
  try {
    const parsed = JSON.parse(e.message) as { error?: { code?: string; message?: string } } | null;
    const msg = parsed?.error?.message;
    if (msg) {
      // The apology is copy, not a sentence to re-case (see GRACEFUL_FAILURE).
      if (msg.startsWith('Apologies')) return msg;
      const cased = titleCase(msg);
      return parsed?.error?.code ? `${cased} (${parsed!.error!.code})` : cased;
    }
  } catch {
    /* not JSON — the message itself is the best we have */
  }
  // A raw transport message ("Failed to fetch", "<!DOCTYPE html>") is a
  // browser's words, not ours: answer it with the apology instead of showing
  // the person something they cannot act on.
  if (isTransportMessage(e.message)) return GRACEFUL_FAILURE;
  return titleCase(e.message);
}

/** Browser/engine strings that mean "the request itself never became an
 *  answer" — these are system failures and get the support line. */
function isTransportMessage(message: string): boolean {
  return /^(Failed to fetch|NetworkError|Load failed|TypeError: Failed to fetch|Unexpected token '<'|Unexpected end of JSON input)/i
    .test(message.trim());
}
